import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import {
  BookmarkAdd as BookmarkAddIcon,
  Bookmark as BookmarkIcon,
  ArrowBackIosNew as ArrowBackIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompanyQuote } from "./hooks/useCompanyQuote";
import { useEarningsTranscriptDates } from "./hooks/useEarningsTranscriptDates";
import { useIsStockInWatchlist } from "../Watchlists/hooks/useIsStockInWatchlist";
import { useSelectedWatchlist } from "../Watchlists/hooks/useSelectedWatchlist";
import { useAddStockToWatchlist } from "../Watchlists/hooks/useAddStockToWatchlist";
import { useRemoveStockFromWatchlist } from "../Watchlists/hooks/useRemoveStockFromWatchlist";
import WatchlistMembershipPicker from "../Watchlists/WatchlistMembershipPicker";
import WatchlistNameModal from "../Watchlists/WatchlistNameModal";
import { hapticError, hapticSelection } from "../../../lib/haptics";
import { getSymbolImageUrl } from "../../../lib/symbolImage";
import {
  EARNINGS_STORAGE_KEY,
  EARNINGS_PREFERRED_WATCHLIST_NAME,
} from "../Earnings/earningsSelection";
import SearchBar from "../../common/SearchBar/SearchBar";
import { EarningsView } from "./components/EarningsViewToggle";
import { viewState } from "../Earnings/earningsNavigation";
import ViewCompanyEarnings from "./ViewCompanyEarnings";
import ViewCompanyEarningsTranscript from "./ViewCompanyEarningsTranscript";
import { TranscriptMode } from "./components/TranscriptModeToggle";
import TranscriptControls from "./components/TranscriptControls";
import { getCurrentQuarter, QuarterOption } from "./utils/quarterUtils";
import { EarningsChartData } from "./hooks/useCompanyEarnings";
import { Theme } from "@mui/material/styles";
import { useMembership } from "../../../contexts/MembershipContext";
import { isNative } from "../../../lib/platform";
import { MOBILE_TOPBAR_HEIGHT } from "../../../contexts/SidebarContext";
import {
  BOTTOM_TAB_BAR_HEIGHT,
  BOTTOM_TAB_BAR_SAFE_AREA,
} from "../../Native/BottomTabBar";

// Flat UI Color System - Theme Aware
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth (similar in both themes)
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Negative/Decline States (similar in both themes)
    negative: "#ef4444", // Clear red for losses/negative data
    negativeHover: "#dc2626", // Darker red for hover

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9", // Very subtle borders

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text

    // Accent/Information (slightly adjusted for dark)
    accent: isDark ? "#60a5fa" : "#3b82f6", // Brighter blue in dark mode
    warning: isDark ? "#fbbf24" : "#f59e0b", // Brighter orange in dark mode
  };
};

// Flat UI Typography System
const flatUITypography = {
  // Display - Company name, main price
  display: {
    fontSize: "3rem", // 48px
    fontWeight: 700, // Bold
    lineHeight: 1.1,
    letterSpacing: "-0.02em",
  },

  // Heading 1 - Tab titles, section headers
  h1: {
    fontSize: "2rem", // 32px
    fontWeight: 600, // Semi-bold
    lineHeight: 1.2,
  },

  // Heading 2 - Chart titles, metric labels
  h2: {
    fontSize: "1.25rem", // 20px
    fontWeight: 600,
    lineHeight: 1.3,
  },

  // Body - General content
  body: {
    fontSize: "1rem", // 16px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },

  // Caption - Small metrics, timestamps
  caption: {
    fontSize: "0.875rem", // 14px
    fontWeight: 400,
    lineHeight: 1.4,
  },

  // Label - Form labels, tiny text
  label: {
    fontSize: "0.75rem", // 12px
    fontWeight: 500, // Medium
    lineHeight: 1.3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
};

// Function to get beat rate color based on performance
const getBeatRateColor = (beatRate: number): string => {
  if (beatRate >= 80) return "#22c55e";
  if (beatRate >= 65) return "#84cc16";
  if (beatRate >= 50) return "#f59e0b";
  if (beatRate >= 35) return "#f97316";
  return "#ef4444";
};

// Flat UI Compact Header Component for Earnings
const EarningsCompactHeader: React.FC<{
  ticker: string;
  beatRate?: number;
  showBeatRate?: boolean;
}> = ({ ticker, beatRate, showBeatRate = false }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isMembershipActive } = useMembership();
  const navigate = useNavigate();

  // Fetch company quote data for the header
  const { data: companyData, isLoading } = useCompanyQuote(ticker);
  const company = Array.isArray(companyData) ? companyData[0] : companyData;

  // Bookmarking now targets the same named stock watchlists as the rest of the
  // app, rather than the retired standalone earnings list.
  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { isInWatchlist: isWatched, memberships } = useIsStockInWatchlist(ticker);
  // This header only renders under /earnings/**, so it binds to the Earnings
  // selection rather than the Dashboard's. Bookmarking here and finding the
  // company absent on the way back to Earnings reads as a broken button.
  const { watchlists, selectedWatchlistId, selectWatchlist } = useSelectedWatchlist({
    storageKey: EARNINGS_STORAGE_KEY,
    preferName: EARNINGS_PREFERRED_WATCHLIST_NAME,
  });

  // With a single watchlist there's nothing to choose, so the bookmark stays a
  // one-tap toggle. Only once the user has several does picking make sense.
  const hasMultipleWatchlists = watchlists.length > 1;
  const singleMembership = memberships[0];
  const targetWatchlistId = singleMembership?.watchlistId ?? selectedWatchlistId;

  const addMutation = useAddStockToWatchlist(targetWatchlistId ?? "");
  const removeMutation = useRemoveStockFromWatchlist(targetWatchlistId ?? "");
  const isWatchLoading = addMutation.isPending || removeMutation.isPending;
  // With no watchlist there is nowhere to save to. The handler already bailed,
  // but the button stayed enabled — so a tap looked accepted and did nothing.
  const canWatch = hasMultipleWatchlists || targetWatchlistId !== null;

  const handleWatchClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleWatchlists) {
      setPickerAnchorEl(event.currentTarget);
      return;
    }

    if (!targetWatchlistId) return;

    const callbacks = {
      onSuccess: () => hapticSelection(),
      onError: () => hapticError(),
    };

    if (isWatched && singleMembership) {
      removeMutation.mutate(
        { holdingId: singleMembership.holdingId, ticker },
        callbacks,
      );
      return;
    }
    addMutation.mutate({ ticker }, callbacks);
  };

  const watchlistSurfaces = (
    <>
      <WatchlistMembershipPicker
        open={!!pickerAnchorEl}
        anchorEl={pickerAnchorEl}
        onClose={() => setPickerAnchorEl(null)}
        ticker={ticker}
        watchlists={watchlists}
        memberships={memberships}
        onCreate={() => setIsCreateOpen(true)}
      />
      <WatchlistNameModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(created) => selectWatchlist(created.id)}
      />
    </>
  );

  if (isLoading || !company) {
    return (
      <Box
        sx={{
          height: isMobile ? 56 : 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: isMobile ? 2 : 3,
          borderBottom: `1px solid ${flatUIColors.border}`,
          backgroundColor: flatUIColors.background,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Typography sx={{ color: flatUIColors.textSecondary, fontSize: isMobile ? "0.875rem" : "1rem" }}>
          Loading company data...
        </Typography>
      </Box>
    );
  }

  const { symbol, name, exchange } = company;

  // MOBILE: Compact single-row layout
  if (isMobile) {
    return (
      <Box
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          borderBottom: `1px solid ${flatUIColors.border}`,
          backgroundColor: flatUIColors.background,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        {/* Left: Back + Logo + Company Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          {/* Back to the earnings search — without this, native users must
              round-trip through the tab bar and retype their search */}
          {/* Web only — the native shell's top bar already carries back */}
          {!isNative() && (
            <IconButton
              size="small"
              onClick={() => {
                hapticSelection();
                navigate("/earnings", { state: viewState("home") });
              }}
              aria-label="Back to earnings search"
              sx={{ ml: -1, flexShrink: 0, color: flatUIColors.textSecondary }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <Box sx={{ position: "relative", width: 32, height: 32, flexShrink: 0 }}>
            <Box
              component="img"
              src={getSymbolImageUrl(symbol)}
              sx={{
                width: 32,
                height: 32,
                objectFit: "contain",
              }}
              alt={`${symbol} logo`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) {
                  fallback.style.display = "flex";
                }
              }}
            />
            <Box
              sx={{
                display: "none",
                position: "absolute",
                top: 0,
                left: 0,
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(34, 197, 94, 0.1)"
                    : "rgba(34, 197, 94, 0.05)",
                border:
                  theme.palette.mode === "dark"
                    ? "2px solid rgba(34, 197, 94, 0.2)"
                    : "2px solid rgba(34, 197, 94, 0.1)",
                fontSize: "1rem",
                fontWeight: 700,
                color: "#22c55e",
              }}
            >
              {symbol?.charAt(0) ?? '?'}
            </Box>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: flatUIColors.textPrimary,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: flatUIColors.textSecondary,
                  fontWeight: 500,
                }}
              >
                {symbol}
              </Typography>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.125,
                  backgroundColor: flatUIColors.surface,
                  border: `1px solid ${flatUIColors.border}`,
                  borderRadius: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    color: flatUIColors.textSecondary,
                  }}
                >
                  {exchange}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right: Bookmark */}
        <IconButton
          size="small"
          onClick={handleWatchClick}
          disabled={isWatchLoading || !canWatch}
          aria-label={isWatched ? "Remove from watchlist" : "Add to watchlist"}
          sx={{
            color: isWatched ? flatUIColors.primary : flatUIColors.textSecondary,
            flexShrink: 0,
            "&:hover": {
              backgroundColor: flatUIColors.surface,
              color: isWatched ? flatUIColors.primaryHover : flatUIColors.primary,
            },
            "&:disabled": {
              opacity: 0.5,
            },
          }}
        >
          {isWatched ? <BookmarkIcon /> : <BookmarkAddIcon />}
        </IconButton>
        {watchlistSurfaces}
      </Box>
    );
  }

  // DESKTOP: Full layout
  return (
    <Box
      sx={{
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        borderBottom: `1px solid ${flatUIColors.border}`,
        backgroundColor: flatUIColors.background,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left Section - Back + Company Info and Price */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flex: "0 0 auto",
        }}
      >
        {/* Web only — the native shell's top bar already carries back */}
        {!isNative() && (
          <IconButton
            size="small"
            onClick={() => {
              hapticSelection();
              navigate("/earnings", { state: viewState("home") });
            }}
            aria-label="Back to earnings search"
            sx={{ ml: -1, flexShrink: 0, color: flatUIColors.textSecondary }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Box sx={{ position: "relative" }}>
          <Box
            component="img"
            src={getSymbolImageUrl(symbol)}
            sx={{
              width: 40,
              height: 40,
              objectFit: "contain",
            }}
            alt={`${symbol} logo`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.textContent = symbol?.charAt(0) ?? '?';
                fallback.style.display = "flex";
              }
            }}
          />
          <Box
            sx={{
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.05)",
              border:
                theme.palette.mode === "dark"
                  ? "2px solid rgba(34, 197, 94, 0.2)"
                  : "2px solid rgba(34, 197, 94, 0.1)",
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#22c55e",
            }}
          ></Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{
              ...flatUITypography.body,
              fontWeight: 600,
              color: flatUIColors.textPrimary,
              lineHeight: 1,
            }}
          >
            {name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                ...flatUITypography.caption,
                color: flatUIColors.textSecondary,
                fontWeight: 500,
              }}
            >
              {symbol}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                backgroundColor: flatUIColors.surface,
                border: `1px solid ${flatUIColors.border}`,
                borderRadius: 0, // Sharp corners
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  ...flatUITypography.label,
                  color: flatUIColors.textSecondary,
                  fontSize: "0.65rem",
                }}
              >
                {exchange}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Watch Bookmark - Flat UI compliant */}
        <IconButton
          size="small"
          onClick={handleWatchClick}
          disabled={isWatchLoading || !canWatch}
          aria-label={isWatched ? "Remove from watchlist" : "Add to watchlist"}
          sx={{
            color: isWatched ? flatUIColors.primary : flatUIColors.textSecondary, // Green when watched, muted when not
            "&:hover": {
              backgroundColor: flatUIColors.surface,
              color: isWatched ? flatUIColors.primaryHover : flatUIColors.primary, // Follow flat UI color system
            },
            "&:disabled": {
              opacity: 0.5,
            },
            ml: 0.25, // Minimal margin to sit super close
            p: 0.25,  // Minimal padding - almost flush
          }}
        >
          {isWatched ? <BookmarkIcon /> : <BookmarkAddIcon />}
        </IconButton>
        {watchlistSurfaces}

        {/* Beat Rate - Only show on Earnings tab */}
        {showBeatRate && beatRate !== undefined && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: 2,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: flatUIColors.surface,
              border: `1px solid ${flatUIColors.border}`,
            }}
          >
            <Typography
              sx={{
                ...flatUITypography.caption,
                color: flatUIColors.textSecondary,
                fontWeight: 500,
              }}
            >
              Beat Rate
            </Typography>
            <Typography
              sx={{
                ...flatUITypography.caption,
                fontWeight: 700,
                color: getBeatRateColor(beatRate),
              }}
            >
              {beatRate.toFixed(1)}%
            </Typography>
          </Box>
        )}

        {/* Divider */}
        <Box
          sx={{
            width: "1px",
            height: "40px",
            backgroundColor: flatUIColors.border,
            mx: 1,
          }}
        />

        {/* Search Bar - Moved to left side */}
        <Box
          sx={{
            width: "350px",
            "& .MuiAutocomplete-root": {
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                borderRadius: 1.5,
                height: "36px",
                border: `1px solid ${flatUIColors.border}`,
                transition: "all 0.2s ease",
                "& fieldset": {
                  border: "none",
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "rgba(0, 0, 0, 0.01)",
                  borderColor: flatUIColors.textSecondary,
                },
                "&.Mui-focused": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                  border: `1px solid ${flatUIColors.primary}`,
                },
                "& input": {
                  fontSize: "0.875rem",
                  color: flatUIColors.textPrimary,
                  padding: "8px 12px",
                  "&::placeholder": {
                    color: flatUIColors.textMuted,
                    opacity: 1,
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: flatUIColors.textMuted,
                },
              },
            },
          }}
        >
          <Tooltip
            title={!isMembershipActive ? "Available with Insight Pro" : ""}
            arrow
          >
            <span style={{ display: "block", width: "100%" }}>
              <SearchBar
                placeholder="Search..."
                width="100%"
                variant="topbar"
                mode="earnings"
                disabled={!isMembershipActive}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

    </Box>
  );
};

interface ViewCompanyEarningsWrapperProps {
  ticker: string;
  symbol: string;
  chartData: EarningsChartData[];
}

interface CompanyEarningsState {
  view: EarningsView;
  transcript: {
    year: number;
    quarter: number;
    mode: TranscriptMode;
  };
}

const ViewCompanyEarningsWrapper: React.FC<ViewCompanyEarningsWrapperProps> = ({
  ticker,
  symbol,
  chartData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const flatUIColors = getFlatUIColors(theme);
  const location = useLocation();

  // Calculate beat rate from chart data
  const beatRate = useMemo(() => {
    const actualData = chartData.filter((d) => d.epsActual !== null);
    if (actualData.length === 0) return undefined;
    const beats = actualData.filter((d) => d.beatStatus === "beat").length;
    return (beats / actualData.length) * 100;
  }, [chartData]);

  // Parse initial state from URL parameters once on mount
  const initialState = useMemo((): CompanyEarningsState => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get("view") as EarningsView;
    const yearParam = searchParams.get("year");
    const quarterParam = searchParams.get("quarter");
    const modeParam = searchParams.get("mode") as TranscriptMode;

    // Get default quarter if not provided - use most recent completed quarter
    const completedQuarter = getCurrentQuarter();
    const defaultYear = completedQuarter.year;
    const defaultQuarter = completedQuarter.quarter;

    return {
      view: viewParam || "transcripts",
      transcript: {
        year: yearParam ? parseInt(yearParam) : defaultYear,
        quarter: quarterParam ? parseInt(quarterParam) : defaultQuarter,
        mode: modeParam || "ai-summary",
      },
    };
  }, [location.search]);

  const [state, setState] = useState<CompanyEarningsState>(initialState);

  // Fetch transcript dates for TopBar quarter selector
  const {
    data: transcriptDatesData,
    isLoading: isLoadingDates,
    error: datesError,
  } = useEarningsTranscriptDates(symbol);

  // Transform transcript dates to quarter options format
  const quarterOptions = useMemo((): QuarterOption[] => {
    if (!transcriptDatesData) return [];
    
    return transcriptDatesData.map(item => ({
      year: parseInt(item.value.split('-')[0]),        // Calendar year from calculated value
      quarter: parseInt(item.value.split('-')[1]),     // Calendar quarter from calculated value  
      label: item.label,            // Calendar label (e.g., "Q1 2025")
      value: item.value,            // Calendar-based value for selection (e.g., "2025-1")
      isCurrentQuarter: item.isCurrentQuarter,
    }));
  }, [transcriptDatesData]);

  // Create selected quarter string from state
  const selectedQuarter = `${state.transcript.year}-${state.transcript.quarter}`;

  const handleViewChange = (view: EarningsView) => {
    setState((prev) => ({
      ...prev,
      view,
    }));
  };

  const handleTranscriptStateChange = (
    updates: Partial<CompanyEarningsState["transcript"]>
  ) => {
    setState((prev) => ({
      ...prev,
      transcript: {
        ...prev.transcript,
        ...updates,
      },
    }));
  };

  const handleQuarterSelect = (quarterOption: QuarterOption) => {
    setState((prev) => ({
      ...prev,
      transcript: {
        ...prev.transcript,
        year: quarterOption.year,
        quarter: quarterOption.quarter,
      },
    }));
  };

  // Convert view to tab index
  const tabValue = state.view === "transcripts" ? 0 : 1;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    hapticSelection();
    const newView = newValue === 0 ? "transcripts" : "earnings";
    handleViewChange(newView);
  };

  // The native shell adds a bottom tab bar (plus notch/home-indicator insets)
  // that this fixed-height viewport must clear; on web both terms are zero.
  const nativeChromeOffset = isNative()
    ? `${BOTTOM_TAB_BAR_HEIGHT}px + env(safe-area-inset-top, 0px) + ${BOTTOM_TAB_BAR_SAFE_AREA}`
    : "0px";

  return (
    <Box
      data-tour="earnings-ticker-view"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: {
          // Parentheses matter: the offset is a sum, and without them only
          // its first term would be subtracted (the rest would be ADDED)
          xs: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px - (${nativeChromeOffset}))`,
          lg: "100vh",
        },
        backgroundColor: flatUIColors.background,
      }}
    >
      {/* Compact Header */}
      <EarningsCompactHeader
        ticker={ticker}
        beatRate={beatRate}
        showBeatRate={state.view === "earnings"}
      />

      {/* Main Layout */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Main Content Area with Tabs */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: flatUIColors.background,
          }}
        >
          {/* Flat Tab Navigation with Controls */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              paddingTop: { xs: 1.5, sm: 2 },
              paddingBottom: { xs: 1.5, sm: 2 },
              backgroundColor: flatUIColors.background,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 1.5, sm: 0 },
              minHeight: { xs: "auto", sm: 60 },
            }}
          >
            {/* Left side - Tabs */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: { xs: 0.5, sm: 1 },
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  ...flatUITypography.body,
                  fontWeight: 500,
                  color: flatUIColors.textSecondary,
                  py: 0,
                  px: { xs: 1.5, sm: 2 },
                  minHeight: { xs: 28, sm: 32 },
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                  border: `1px solid ${flatUIColors.border}`,
                  borderRadius: 0.5,
                  transition: "all 200ms linear",
                  "&.Mui-selected": {
                    color: flatUIColors.primary,
                    fontWeight: 600,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                    borderColor: flatUIColors.primary,
                  },
                  "&:hover": {
                    color: flatUIColors.textPrimary,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#212121" : "#e5e5e5",
                  },
                },
              }}
            >
              <Tab label="Transcripts" data-tour="earnings-transcripts-tab" />
              <Tab label="Earnings" />
            </Tabs>

            {/* Right side - Transcript Controls (only show on Transcripts tab) */}
            {tabValue === 0 && (
              <TranscriptControls
                quarterOptions={quarterOptions}
                selectedQuarter={selectedQuarter}
                onQuarterSelect={handleQuarterSelect}
                mode={state.transcript.mode}
                onModeChange={(mode) => handleTranscriptStateChange({ mode })}
                isLoading={isLoadingDates}
              />
            )}
          </Box>

          {/* Tab Content with Flat Background */}
          <Box
            sx={{
              flex: 1,
              // On mobile for transcript tab, don't scroll here - let the card handle it
              overflow: isMobile && tabValue === 0 ? "hidden" : "auto",
              backgroundColor: flatUIColors.background,
            }}
          >
            {tabValue === 0 && (
              <ViewCompanyEarningsTranscript
                symbol={symbol}
                year={state.transcript.year}
                quarter={state.transcript.quarter}
                mode={state.transcript.mode}
                onStateChange={handleTranscriptStateChange}
                quarterOptions={quarterOptions}
                selectedQuarter={selectedQuarter}
                onQuarterSelect={handleQuarterSelect}
                isLoadingDates={isLoadingDates}
              />
            )}
            {tabValue === 1 && (
              <Box sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 3 }}>
                <ViewCompanyEarnings ticker={ticker} symbol={symbol} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ViewCompanyEarningsWrapper;
